import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type AdminCategory } from '../lib/api.ts';

interface CategoriesEditorProps {
  token: string;
  onUnauthorised: () => void;
  /** Treatments live inside categories, so a change here refreshes that list. */
  onChanged?: () => void | Promise<void>;
}

interface Draft {
  name: string;
  blurb: string;
}

/**
 * The groups treatments are listed under, and the sentence beneath each heading
 * on the public page. Both were fixed by the seed until now.
 */
export function CategoriesEditor({ token, onUnauthorised, onChanged }: CategoriesEditorProps) {
  const [categories, setCategories] = useState<AdminCategory[] | null>(null);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState<Draft>({ name: '', blurb: '' });
  const [busyId, setBusyId] = useState<number | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.admin.getCategories(token);
      setCategories(result.categories);
      setDrafts(
        Object.fromEntries(
          result.categories.map((c) => [c.id, { name: c.name, blurb: c.blurb ?? '' }]),
        ),
      );
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) return onUnauthorised();
      setError('Categories could not be loaded.');
    }
  }, [token, onUnauthorised]);

  useEffect(() => {
    void load();
  }, [load]);

  function isDirty(category: AdminCategory): boolean {
    const draft = drafts[category.id];
    if (!draft) return false;
    return draft.name.trim() !== category.name || draft.blurb.trim() !== (category.blurb ?? '');
  }

  async function save(category: AdminCategory) {
    const draft = drafts[category.id];
    if (!draft || draft.name.trim().length < 2) return;

    setBusyId(category.id);
    setError(null);
    try {
      await api.admin.updateCategory(token, category.id, {
        name: draft.name.trim(),
        blurb: draft.blurb.trim() || null,
      });
      setMessage(`${draft.name.trim()} updated.`);
      await load();
      await onChanged?.();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) return onUnauthorised();
      setError(caught instanceof ApiError ? caught.message : 'That could not be saved.');
    } finally {
      setBusyId(null);
    }
  }

  async function add() {
    if (newDraft.name.trim().length < 2) return;
    setBusyId('new');
    setError(null);
    try {
      const created = await api.admin.createCategory(token, {
        name: newDraft.name.trim(),
        blurb: newDraft.blurb.trim() || undefined,
      });
      setMessage(`${created.name} added. Add treatments to it and it appears on the website.`);
      setNewDraft({ name: '', blurb: '' });
      setAdding(false);
      await load();
      await onChanged?.();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) return onUnauthorised();
      setError(caught instanceof ApiError ? caught.message : 'That could not be added.');
    } finally {
      setBusyId(null);
    }
  }

  async function move(category: AdminCategory, direction: -1 | 1) {
    if (!categories) return;
    const ordered = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = ordered.findIndex((c) => c.id === category.id);
    const swapWith = ordered[index + direction];
    if (!swapWith) return;

    setBusyId(category.id);
    setError(null);
    try {
      // Swap the two sort values, so ordering stays stable rather than drifting.
      await api.admin.updateCategory(token, category.id, { sortOrder: swapWith.sortOrder });
      await api.admin.updateCategory(token, swapWith.id, { sortOrder: category.sortOrder });
      await load();
      await onChanged?.();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) return onUnauthorised();
      setError('The order could not be changed.');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(category: AdminCategory) {
    if (!window.confirm(`Delete the ${category.name} category?`)) return;
    setBusyId(category.id);
    setError(null);
    try {
      await api.admin.deleteCategory(token, category.id);
      setMessage(`${category.name} deleted.`);
      await load();
      await onChanged?.();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) return onUnauthorised();
      // The API explains which treatments are in the way; show it verbatim.
      setError(caught instanceof ApiError ? caught.message : 'That could not be deleted.');
    } finally {
      setBusyId(null);
    }
  }

  const ordered = [...(categories ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <section className="stack-lg">
      <div className="treatments__head">
        <div>
          <h3 className="admin__section-title">Categories</h3>
          <p className="field-hint">
            The groups treatments are listed under, and the sentence shown beneath each heading on
            the website. The order here is the order on the page.
          </p>
        </div>
        <button type="button" className="btn btn--ghost" onClick={() => setAdding(true)} disabled={adding}>
          Add category
        </button>
      </div>

      {message && <p className="notice notice--success">{message}</p>}
      {error && <p className="notice notice--error">{error}</p>}

      {adding && (
        <div className="panel admin-form">
          <h4 className="admin-form__title">New category</h4>
          <div className="field">
            <label htmlFor="new-category-name">Name</label>
            <input
              id="new-category-name"
              className="input"
              value={newDraft.name}
              maxLength={80}
              onChange={(event) => setNewDraft((d) => ({ ...d, name: event.target.value }))}
            />
          </div>
          <div className="field">
            <label htmlFor="new-category-blurb">Description</label>
            <textarea
              id="new-category-blurb"
              className="textarea"
              rows={2}
              maxLength={400}
              value={newDraft.blurb}
              placeholder="One sentence, shown under the heading on the website."
              onChange={(event) => setNewDraft((d) => ({ ...d, blurb: event.target.value }))}
            />
          </div>
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={busyId === 'new' || newDraft.name.trim().length < 2}
              onClick={add}
            >
              {busyId === 'new' ? <span className="spinner" /> : 'Add category'}
            </button>
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => {
                setAdding(false);
                setNewDraft({ name: '', blurb: '' });
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!categories && (
        <p className="booking__loading">
          <span className="spinner" /> Loading categories…
        </p>
      )}

      <ul className="category-list">
        {ordered.map((category, index) => {
          const draft = drafts[category.id] ?? { name: category.name, blurb: category.blurb ?? '' };
          return (
            <li key={category.id} className="category-row">
              <div className="category-row__order">
                <button
                  type="button"
                  className="btn btn--quiet"
                  aria-label={`Move ${category.name} up`}
                  disabled={index === 0 || busyId === category.id}
                  onClick={() => move(category, -1)}
                >
                  &#8593;
                </button>
                <button
                  type="button"
                  className="btn btn--quiet"
                  aria-label={`Move ${category.name} down`}
                  disabled={index === ordered.length - 1 || busyId === category.id}
                  onClick={() => move(category, 1)}
                >
                  &#8595;
                </button>
              </div>

              <div className="category-row__fields">
                <div className="field">
                  <label htmlFor={`cat-name-${category.id}`}>Name</label>
                  <input
                    id={`cat-name-${category.id}`}
                    className="input"
                    value={draft.name}
                    maxLength={80}
                    onChange={(event) =>
                      setDrafts((d) => ({
                        ...d,
                        [category.id]: { ...draft, name: event.target.value },
                      }))
                    }
                  />
                </div>
                <div className="field">
                  <label htmlFor={`cat-blurb-${category.id}`}>Description</label>
                  <textarea
                    id={`cat-blurb-${category.id}`}
                    className="textarea"
                    rows={2}
                    maxLength={400}
                    value={draft.blurb}
                    placeholder="Shown under the heading on the website."
                    onChange={(event) =>
                      setDrafts((d) => ({
                        ...d,
                        [category.id]: { ...draft, blurb: event.target.value },
                      }))
                    }
                  />
                </div>
                <p className="field-hint">
                  {category.treatmentCount} treatment{category.treatmentCount === 1 ? '' : 's'}
                </p>
              </div>

              <div className="category-row__actions">
                <button
                  type="button"
                  className="btn"
                  disabled={!isDirty(category) || busyId === category.id || draft.name.trim().length < 2}
                  onClick={() => save(category)}
                >
                  {busyId === category.id ? <span className="spinner" /> : 'Save'}
                </button>
                <button
                  type="button"
                  className="btn btn--danger"
                  disabled={!category.deletable || busyId === category.id}
                  title={
                    category.deletable
                      ? 'Delete this category'
                      : 'Move or delete its treatments first'
                  }
                  onClick={() => remove(category)}
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
